import {useEffect, useRef, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import MoleculeDrawer, {MoleculeDrawerOverlay, moleculeDrawerPlacements} from '@s-ui/react-molecule-drawer'
import {useStepFlow} from '@adv-mt/mt-hook-use-step-flow'
import ChatHistoryList from '@adv-mt/mt-net-chat-history-list'
import NetFeedback from '@adv-mt/mt-net-feedback'
import TrackingPageView from '@adv-mt/mt-tracking-page-view'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import NetChatFooter from './components/NetChatFooter.js'
import NetDrawerHeader from './components/NetDrawerHeader.js'
import NetChatNotification, {netChatNotificationsType} from './components/notification/index.js'
import {useNetLoading} from './context/NetLoadingContext.js'
import {useNetChat} from './hooks/useNetChat.js'
import NetChat from './steps/chat/index.js'
import ContactForm from './steps/contactFrom/index.js'
import {BASE_CLASS, BLOCK_TYPES, getStepsConfig, MESSAGE_ROLES, STEPS} from './config.js'
import {
  getTrackingAccessOldChat,
  getTrackingCloseNet,
  getTrackingMenuClicked,
  getTrackingMessageSent,
  getTrackingNewConversation,
  getTrackingReviewDetailSent
} from './tracking.js'

const NetDrawer = ({
  isOpen,
  onClose,
  onOpen,
  pageName,
  listingSectionId,
  onFavoriteChange,
  initialSearchValue,
  initialSuggestions,
  context,
  headerText
}) => {
  const drawerRef = useRef()
  const containerRef = useRef()

  const {domain, i18n} = useSuiContext()
  const {track, page} = useAnalyticsTrack()
  const [notificationType, setNotificationType] = useState(null)
  const [isVisibleLoginModal, setIsVisibleLoginModal] = useState(false)
  const [initialMessage, setInitialMessage] = useState(null)

  const {currentStep, canGoBack, push, goBack, reset, params} = useStepFlow({
    initialStep: STEPS.CHAT,
    resetOnMount: isOpen
  })

  const {
    chatData,
    chats,
    retrying,
    restoring,
    sendMessage,
    retry,
    loadChat,
    clearChat,
    onAdClick,
    onShowResults,
    onSearchSaved,
    onClickFavorite,
    onReviewSuccess,
    selectedReviewingBlock,
    clearSelectedReviewingBlock,
    notificationError,
    clearNotificationError
  } = useNetChat({
    isOpen,
    i18n,
    context,
    pageName,
    listingSectionId,
    onClose,
    onFavoriteChange,
    onReviewNegative: () => push(STEPS.FEEDBACK),
    onReviewPositive: () => setNotificationType(netChatNotificationsType.FEEDBACK_SUCCESS)
  })

  const {hasNetFinishedLoading, clearNetFinishedLoadingBadge} = useNetLoading()

  const config = domain.get('config')
  const tracking = config.get('tracking')
  const {FEEDBACK_REVIEW_TAGS, NET_TRACKING_PAGE_NAME, NET_MENU_TRACKING_PAGE_NAME} = config.get('COMMON').AI_ASSISTANT

  const STEP_CONFIG = getStepsConfig(i18n)
  const currentConfig = STEP_CONFIG[currentStep] || STEP_CONFIG[STEPS.CHAT]

  // Focus drawer container for keyboard navigation on non-chat steps (chat step has net input auto-focused)
  useEffect(() => {
    if (isOpen && containerRef.current && currentStep !== STEPS.CHAT) containerRef.current.focus()
  }, [isOpen, currentStep])

  // Capture initial search value when drawer opens
  useEffect(() => {
    if (isOpen && initialSearchValue?.text) {
      setInitialMessage(initialSearchValue)
    }
  }, [isOpen, initialSearchValue])

  // Send initial message when restoring is done
  useEffect(() => {
    if (isOpen && !restoring && initialMessage?.text) {
      const {text, messageType} = initialMessage
      onSendMessage(text, messageType)
      setInitialMessage(null) // Clear it immediately after sending to prevent duplicates
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, restoring, initialMessage])

  // Sync notificationError from hook into local notification state
  useEffect(() => {
    if (notificationError) {
      setNotificationType(netChatNotificationsType.GENERIC_ERROR)
      clearNotificationError()
    }
  }, [notificationError, clearNotificationError])

  useEffect(() => {
    if (!isOpen && hasNetFinishedLoading) {
      setNotificationType(netChatNotificationsType.CHAT_RESULTS_AVAILABLE)
    } else if (isOpen && hasNetFinishedLoading) {
      clearNetFinishedLoadingBadge()
    }
  }, [isOpen, hasNetFinishedLoading, clearNetFinishedLoadingBadge])

  const onOpenDrawer = () => {
    setNotificationType(null)
    clearNetFinishedLoadingBadge()
    onOpen()
  }

  const openNewChat = () => {
    track(getTrackingNewConversation(tracking))
    clearChat()
    reset()
    setNotificationType(netChatNotificationsType.NEW_CHAT_SUCCESS)
  }

  const onSendMessage = (value, messageType) => {
    sendMessage(value, messageType, {
      onTrack: (newChatData, messageData) => {
        track(getTrackingMessageSent(tracking, newChatData, messageData))
      }
    })
  }

  const onRetry = () => {
    retry()
  }

  const onEndReviewStep = () => {
    setNotificationType(netChatNotificationsType.FEEDBACK_SUCCESS)
    clearSelectedReviewingBlock()
    goBack()
  }

  const onSuccessReviewStep = (text, selectedTags = []) => {
    const tags = selectedTags.map(value => i18n.t(FEEDBACK_REVIEW_TAGS[value.toUpperCase()]?.label)).join(', ')
    track(
      getTrackingReviewDetailSent(tracking, {
        ...selectedReviewingBlock,
        tags,
        text
      })
    )
    onEndReviewStep()
  }

  const onClickClose = () => {
    track(getTrackingCloseNet(tracking, chatData))

    if (currentStep === STEPS.FEEDBACK) {
      setNotificationType(netChatNotificationsType.FEEDBACK_SUCCESS)
      clearSelectedReviewingBlock()
    }

    onClose()
  }

  const onOpenHistory = () => {
    track(getTrackingMenuClicked(tracking, chatData?.chatId))
    push(STEPS.CHAT_HISTORY)
    domain
      .get('get_page_tracking_data_use_case')
      .execute({page: NET_MENU_TRACKING_PAGE_NAME})
      .then(response => {
        if (response) page(response)
      })
  }

  const onClickContact = contactInformation => {
    push(STEPS.CONTACT_FORM, contactInformation)
  }

  const onSelectChat = chatId => {
    track(getTrackingAccessOldChat(tracking, chatData?.chatId, chatId))
    goBack()
    loadChat(chatId)
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case STEPS.FEEDBACK:
        return <NetFeedback onFinish={onEndReviewStep} onSuccess={onSuccessReviewStep} />
      case STEPS.CHAT_HISTORY:
        return (
          <ChatHistoryList
            chats={chats}
            currentChatId={chatData?.chatId}
            onSelectChat={onSelectChat}
            onNewChat={openNewChat}
          />
        )
      case STEPS.CONTACT_FORM:
        return (
          <ContactForm
            ad={params?.ad}
            message={params?.message}
            extendedTrackingData={params?.extendedTrackingData}
            onClose={goBack}
            pageName={pageName}
          />
        )
      case STEPS.CHAT:
      default:
        return (
          <NetChat
            messages={chatData.content}
            onNewChat={openNewChat}
            onReviewSuccess={onReviewSuccess}
            chatId={chatData?.chatId}
            onShowResults={onShowResults}
            onAdClick={onAdClick}
            onSearchSaved={onSearchSaved}
            setIsVisibleLoginModal={setIsVisibleLoginModal}
            onSubmit={onSendMessage}
            onRetry={onRetry}
            restoring={restoring}
            retrying={retrying}
            onFavoriteChange={onClickFavorite}
            initialSuggestions={initialSuggestions}
            headerText={headerText}
            onClickContact={onClickContact}
          />
        )
    }
  }

  const hasEndChat = chatData.content?.some(
    message => message.role === MESSAGE_ROLES.ASSISTANT && message.blocks?.some(block => block.type === BLOCK_TYPES.END)
  )

  const renderStepFooter = () => {
    switch (currentStep) {
      case STEPS.FEEDBACK:
        return null
      case STEPS.CHAT_HISTORY:
        return null
      case STEPS.CONTACT_FORM:
        return null
      case STEPS.CHAT:
      default:
        return (
          <NetChatFooter
            chatId={chatData?.chatId}
            onSubmit={onSendMessage}
            disabled={restoring || retrying || chatData.content?.some(message => message.loading) || hasEndChat}
          />
        )
    }
  }

  return (
    <>
      <MoleculeDrawerOverlay isVisible={isOpen} />
      <MoleculeDrawer
        ref={drawerRef}
        closeOnOutsideClick={!notificationType && !isVisibleLoginModal}
        isPageScrollable={false}
        isOpen={isOpen}
        onClose={onClickClose}
        placement={moleculeDrawerPlacements.RIGHT}
      >
        {isOpen && (
          <div ref={containerRef} className={BASE_CLASS} tabIndex={0}>
            <NetDrawerHeader
              title={currentConfig.title}
              chatId={chatData?.chatId}
              context={context}
              pageName={pageName}
              showBackButton={canGoBack && currentConfig.showBackButton}
              showHistoryButton={currentConfig.showHistoryButton}
              showExpandButton={currentConfig.showHistoryButton}
              showLogo={currentConfig.showLogo}
              onBack={currentStep === STEPS.FEEDBACK ? onEndReviewStep : goBack}
              onHistory={onOpenHistory}
              onClose={onClickClose}
            />
            <div className={cx(`${BASE_CLASS}-content`, {[`${BASE_CLASS}-content--restoring`]: restoring})}>
              {renderStepContent()}
            </div>
            {renderStepFooter()}
          </div>
        )}
      </MoleculeDrawer>

      <NetChatNotification
        type={notificationType}
        onClose={() => setNotificationType(null)}
        onOpenDrawer={onOpenDrawer}
      />

      {isOpen && <TrackingPageView pageName={NET_TRACKING_PAGE_NAME} />}
    </>
  )
}

NetDrawer.displayName = 'NetDrawer'

NetDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  pageName: PropTypes.string,
  listingSectionId: PropTypes.string,
  onFavoriteChange: PropTypes.func,
  initialSearchValue: PropTypes.objectOf({
    text: PropTypes.string,
    messageType: PropTypes.string
  }),
  initialSuggestions: PropTypes.arrayOf(PropTypes.shape({text: PropTypes.string})),
  context: PropTypes.shape({
    page: PropTypes.shape({
      type: PropTypes.string
    })
  }).isRequired,
  headerText: PropTypes.string
}

export {useReopenDrawerOnSession} from './hooks/useReopenDrawerOnSession.js'
export {NetLoadingProvider, useNetLoading} from './context/NetLoadingContext.js'
export {useNetChat} from './hooks/useNetChat.js'
export {useSkeletonGroups} from './hooks/useSkeletonGroups.js'
export * from './tracking.js'
export default NetDrawer
